import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "../../PermissionsProvider";
import { Workflow, WorkflowService } from "../../services/WorkflowService";
import { ApplicationService } from "../../services/ApplicationService";
import { Application } from "../../model_gen/application";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { PlusIcon } from "lucide-react";
import { toast } from "sonner";
import WorkflowsTable from "./WorkflowsTable";

export interface WorkflowWithApp extends Workflow {
  application_name?: string;
}

const Workflows: React.FC = () => {
  const { apiKey } = usePermissions();
  const navigate = useNavigate();
  const [allWorkflows, setAllWorkflows] = useState<WorkflowWithApp[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [loadingState, setLoadingState] = useState<"loading" | "loaded" | "error">("loading");
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<number | null>(null);
  const pageSize = 10;

  useEffect(() => {
    if (!apiKey) return;
    setLoadingState("loading");

    let fetchedWorkflows: Workflow[] = [];
    let fetchedApps: Application[] = [];
    let loaded = 0;

    const enrichAndSet = () => {
      loaded++;
      if (loaded < 2) return;
      const appMap: Record<number, string> = {};
      fetchedApps.forEach((app) => {
        if (app.id != null) appMap[app.id] = app.application_name;
      });
      const enriched: WorkflowWithApp[] = fetchedWorkflows.map((wf) => ({
        ...wf,
        application_name: wf.application_id ? appMap[wf.application_id] || "Unknown" : undefined,
      }));
      enriched.sort((a, b) => (b.date_created || "").localeCompare(a.date_created || ""));
      setAllWorkflows(enriched);
      setApplications(fetchedApps);
      setLoadingState("loaded");
    };

    const wfSub = WorkflowService.getWorkflows(() => {}, apiKey).subscribe(
      (response: any) => {
        fetchedWorkflows = Array.isArray(response) ? response : [];
        enrichAndSet();
      },
      () => { enrichAndSet(); setLoadingState("error"); }
    );
    const appSub = ApplicationService.getApplications(() => {}, apiKey).subscribe(
      (response: any) => {
        fetchedApps = Array.isArray(response) ? response : (response.applications || []);
        enrichAndSet();
      },
      () => enrichAndSet()
    );
    return () => { wfSub.unsubscribe(); appSub.unsubscribe(); };
  }, [apiKey]);

  const paginatedWorkflows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return allWorkflows.slice(start, start + pageSize);
  }, [allWorkflows, currentPage]);

  const handleDelete = (workflowId: number) => {
    if (!apiKey) return;
    WorkflowService.deleteWorkflow(workflowId, () => {}, apiKey).subscribe(
      () => {
        toast.success("Workflow deleted");
        setAllWorkflows((prev) => prev.filter((w) => w.id !== workflowId));
        setDeleteDialogOpen(null);
      },
      (err: any) => toast.error(`Failed to delete: ${err?.message || "Unknown error"}`)
    );
  };

  const handleCreate = () => navigate("/forge/workflows/new");

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        <Card className="border-border">
          <CardContent className="p-8">
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button
                    onClick={() => navigate("/")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Home
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <button
                    onClick={() => navigate("/forge")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Forge
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">
                    Workflows
                  </span>
                </li>
              </ol>
            </nav>

            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                  Workflows
                </h1>
                <p className="text-lg text-muted-foreground font-gc-reg">
                  Define and manage automated pipelines with agents and tools
                </p>
              </div>
              <Button onClick={handleCreate} className="gap-2">
                <PlusIcon className="w-4 h-4" />
                Create Workflow
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="p-0">
            <WorkflowsTable
              workflows={paginatedWorkflows}
              isLoading={loadingState === "loading"}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              onDelete={handleDelete}
              onCreate={handleCreate}
              deleteDialogOpen={deleteDialogOpen}
              setDeleteDialogOpen={setDeleteDialogOpen}
              pageSize={pageSize}
              totalWorkflows={allWorkflows.length}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Workflows;
